import React, { useState, useEffect } from 'react';
import { activityAPI } from '../../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import './ActivitySchoolMapping.css';

const ActivitySchoolMapping = () => {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [activitySchools, setActivitySchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingMapping, setEditingMapping] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [newMapping, setNewMapping] = useState({
    schoolCode: '',
    isEligible: true,
    remarks: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [ppdFilter, setPpdFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('all'); // 'all', 'ppd', 'dun', 'par'
  const [bulkData, setBulkData] = useState({
    ppd: '',
    dunCode: '',
    parCode: '',
    remarks: ''
  });

  useEffect(() => {
    loadActivity();
    loadActivitySchools();
  }, [activityId, currentPage, pageSize, searchTerm, ppdFilter]);

  const loadActivity = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await activityAPI.getActivity(activityId);
      if (response.success) {
        setActivity(response.data);
      } else {
        setError(response.message || 'Failed to load activity');
      }
    } catch (err) {
      console.error('Load activity error:', err);
      setError('An error occurred while loading the activity');
    } finally {
      setLoading(false);
    }
  };

  const loadActivitySchools = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await activityAPI.getActivitySchools(activityId, currentPage, pageSize, searchTerm, ppdFilter);
      if (response.success) {
        setActivitySchools(response.data.data);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
      } else {
        setError(response.message || 'Failed to load activity schools');
      }
    } catch (err) {
      console.error('Load activity schools error:', err);
      setError('An error occurred while loading activity schools');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMapping = () => {
    setModalMode('create');
    setEditingMapping(null);
    setNewMapping({
      schoolCode: '',
      isEligible: true,
      remarks: ''
    });
    setShowModal(true);
  };

  const handleEditMapping = (activitySchool) => {
    setModalMode('edit');
    setEditingMapping(activitySchool);
    setNewMapping({
      schoolCode: activitySchool.schoolCode,
      isEligible: activitySchool.isEligible,
      remarks: activitySchool.remarks
    });
    setShowModal(true);
  };

  const handleRemoveSchool = async (activitySchoolId) => {
    if (window.confirm('Are you sure you want to remove this school from the activity?')) {
      try {
        const response = await activityAPI.removeSchoolFromActivity(activitySchoolId);
        if (response.success) {
          loadActivitySchools();
          console.log('School removed from activity successfully');
        } else {
          setError(response.message || 'Failed to remove school from activity');
        }
      } catch (err) {
        console.error('Remove school error:', err);
        setError('An error occurred while removing school from activity');
      }
    }
  };

  const handleToggleEligibility = async (activitySchoolId, isEligible) => {
    try {
      const newEligibleValue = !isEligible;
      const response = await activityAPI.updateSchoolEligibility(activitySchoolId, { 
        isEligible: newEligibleValue 
      });
      
      if (response.success) {
        loadActivitySchools();
        console.log(`Eligibility for mapping ${activitySchoolId} updated successfully`);
      } else {
        setError(response.message || 'Failed to update eligibility');
      }
    } catch (err) {
      console.error('Toggle eligibility error:', err);
      setError('An error occurred while updating eligibility');
    }
  };

  const handleSaveMapping = async () => {
    try {
      let response;
      
      if (modalMode === 'create') {
        response = await activityAPI.mapSchoolToActivity(Number(activityId), {
          schoolCode: newMapping.schoolCode,
          isEligible: newMapping.isEligible ? 1 : 0,
          remarks: newMapping.remarks
        });
      } else {
        response = await activityAPI.updateSchoolEligibility(editingMapping.id, { 
          isEligible: newMapping.isEligible ? 1 : 0 
        });
      }

      if (response.success) {
        setShowModal(false);
        setNewMapping({ schoolCode: '', isEligible: true, remarks: '' });
        loadActivitySchools();
        console.log(`School ${modalMode === 'create' ? 'mapped' : 'updated'} successfully`);
      } else {
        setError(response.message || `Failed to ${modalMode} school mapping`);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(`An error occurred while ${modalMode === 'create' ? 'mapping' : 'updating'} school`);
    }
  };

  const handleBulkAction = (action) => {
    setBulkAction(action);
    setBulkData({
      ppd: '',
      dunCode: '',
      parCode: '',
      remarks: ''
    });
    setShowBulkModal(true);
  };

  const handleExecuteBulkAction = async () => {
    try {
      let response;
      const data = {
        remarks: bulkData.remarks
      };

      switch (bulkAction) {
        case 'all':
          response = await activityAPI.bulkMapAllSchools(Number(activityId), data);
          break;
        case 'ppd':
          response = await activityAPI.bulkMapByPpd(Number(activityId), { 
            ...data, 
            ppd: bulkData.ppd 
          });
          break;
        case 'dun':
          response = await activityAPI.bulkMapByDun(Number(activityId), { 
            ...data, 
            dunCode: bulkData.dunCode 
          });
          break;
        case 'par':
          response = await activityAPI.bulkMapByPar(Number(activityId), { 
            ...data, 
            parCode: bulkData.parCode 
          });
          break;
        default:
          setError('Invalid bulk action');
          return;
      }

      if (response.success) {
        setShowBulkModal(false);
        loadActivitySchools();
        alert(`Bulk operation completed: ${response.data} schools mapped.`);
      } else {
        setError(response.message || 'Failed to execute bulk mapping');
      }
    } catch (err) {
      console.error('Bulk action error:', err);
      setError('An error occurred while executing bulk mapping');
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading && !activitySchools.length) {
    return (
      <div className="activity-school-mapping">
        <div className="loading">Loading activity schools...</div>
      </div>
    );
  }

  return (
    <div className="activity-school-mapping">
      {/* Header */}
      <div className="activity-school-mapping-header">
        <div className="header-info">
          <h2><i className="fas fa-link"></i> Activity-School Mapping</h2>
          <p>Manage which schools participate in the selected activity</p>
        </div>
        <div className="activity-details">
          {activity && (
            <>
              <h3>{activity.activityName}</h3>
              <div className="activity-info">
                <span className="badge badge-primary">Year: {activity.enrolYear}</span>
                <span className={`badge ${activity.status === 'Active' ? 'badge-success' : 'badge-secondary'}`}>
                  {activity.status}
                </span>
                <span className="badge badge-info">Mapped Schools: {totalCount}</span>
              </div>
            </>
          )}
        </div>
        <button 
          onClick={() => navigate('/activity-management')}
          className="btn-back"
        >
          <i className="fas fa-arrow-left"></i> Back to Activities
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Bulk Actions */}
      <div className="bulk-actions-section">
        <h3><i className="fas fa-bolt"></i> Bulk Actions</h3>
        <div className="bulk-buttons">
          <button 
            onClick={() => handleBulkAction('all')}
            className="btn-bulk btn-all"
            title="Map all schools in the system to this activity"
          >
            <i className="fas fa-globe"></i> Map All Schools
          </button>
          <button 
            onClick={() => handleBulkAction('ppd')}
            className="btn-bulk btn-ppd"
            title="Map schools by PPD (Education District)"
          >
            <i className="fas fa-building"></i> Map by PPD
          </button>
          <button 
            onClick={() => handleBulkAction('dun')}
            className="btn-bulk btn-dun"
            title="Map schools by DUN (State Legislative Assembly)"
          >
            <i className="fas fa-map-marker-alt"></i> Map by DUN
          </button>
          <button 
            onClick={() => handleBulkAction('par')}
            className="btn-bulk btn-par"
            title="Map schools by Parliament constituency"
          >
            <i className="fas fa-university"></i> Map by Parliament
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="activity-school-mapping-toolbar">
        <div className="filters-section">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search schools (code, name)..."
            className="search-input"
          />
          <select
            value={ppdFilter}
            onChange={(e) => setPpdFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All PPDs</option>
            {/* This would typically be populated from API */}
          </select>
          <button onClick={handleSearch} className="btn-search">
            <i className="fas fa-search"></i> Filter
          </button>
        </div>
        <button onClick={handleCreateMapping} className="btn-add-school">
          <i className="fas fa-plus"></i> Add Individual School
        </button>
      </div>

      {/* Schools Table */}
      <div className="table-container">
        <table className="master-data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>School Code</th>
              <th>School Name</th>
              <th>PPD</th>
              <th>DUN</th>
              <th>Eligible</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {activitySchools.length > 0 ? (
              activitySchools.map((activitySchool, index) => (
                <tr key={activitySchool.id}>
                  <td>{index + 1 + (currentPage - 1) * pageSize}</td>
                  <td><strong>{activitySchool.schoolCode}</strong></td>
                  <td>{activitySchool.schoolName}</td>
                  <td>{activitySchool.ppd || '-'}</td>
                  <td>{activitySchool.dunCode || '-'}</td>
                  <td>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id={`eligibility-${activitySchool.id}`}
                        checked={activitySchool.isEligible}
                        onChange={() => handleToggleEligibility(activitySchool.id, activitySchool.isEligible)}
                        className="toggle-input"
                      />
                      <label 
                        htmlFor={`eligibility-${activitySchool.id}`} 
                        className={`toggle-label ${activitySchool.isEligible ? 'enabled' : 'disabled'}`}
                      >
                        {activitySchool.isEligible ? 'Yes' : 'No'}
                      </label>
                    </div>
                  </td>
                  <td>{activitySchool.remarks || '-'}</td>
                  <td className="actions-column">
                    <button
                      onClick={() => handleEditMapping(activitySchool)}
                      className="btn-action btn-edit"
                      title="Edit Mapping"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleRemoveSchool(activitySchool.id)}
                      className="btn-action btn-remove"
                      title="Remove School"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">No schools mapped to this activity yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({totalCount} total schools)
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-page"
            >
              Previous
            </button>
            <span className="page-input">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => setCurrentPage(Math.max(1, Math.min(totalPages, Number(e.target.value))))}
                min="1"
                max={totalPages}
                className="page-number-input"
              />
              <span>of {totalPages}</span>
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Last
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      {/* Modal for Add/Edit Mapping */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add School to Activity' : `Edit Mapping: ${newMapping.schoolCode}`}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {modalMode === 'create' ? (
                <div className="form-group">
                  <label htmlFor="schoolCode">School *</label>
                  <input
                    id="schoolCode"
                    type="text"
                    value={newMapping.schoolCode}
                    onChange={(e) => setNewMapping({ ...newMapping, schoolCode: e.target.value.trim().toUpperCase() })}
                    placeholder="Enter school code (e.g., XBA1001)"
                    maxLength={50}
                    className="form-control"
                  />
                  <small className="form-help">Enter the school code to map to this activity</small>
                </div>
              ) : (
                <div className="form-group">
                  <label>School Code</label>
                  <div className="readonly-field">{editingMapping?.schoolCode}</div>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newMapping.isEligible}
                    onChange={(e) => setNewMapping({ ...newMapping, isEligible: e.target.checked })}
                    className="checkbox-input"
                  />
                  Eligible to Participate
                </label>
                <small className="form-help">Check if school can participate in this activity</small>
              </div>

              <div className="form-group">
                <label htmlFor="remarks">Remarks</label>
                <textarea
                  id="remarks"
                  value={newMapping.remarks}
                  onChange={(e) => setNewMapping({ ...newMapping, remarks: e.target.value.trim() })}
                  placeholder="Enter remarks or notes (optional)"
                  maxLength={200}
                  rows={3}
                  className="form-control"
                />
                <small className="form-help">Additional notes about this school mapping</small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveMapping}
                disabled={modalMode === 'create' && !newMapping.schoolCode.trim()}
                className="btn btn-primary"
              >
                {modalMode === 'create' ? 'Map School' : 'Update Mapping'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Bulk Actions */}
      {showBulkModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>
                {bulkAction === 'all' && 'Map All Schools'}
                {bulkAction === 'ppd' && 'Map Schools by PPD'}
                {bulkAction === 'dun' && 'Map Schools by DUN'}
                {bulkAction === 'par' && 'Map Schools by Parliament'}
              </h3>
              <button
                onClick={() => setShowBulkModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {(bulkAction === 'ppd' || bulkAction === 'dun' || bulkAction === 'par') && (
                <div className="form-group">
                  <label htmlFor={`${bulkAction}Code`}>
                    {bulkAction === 'ppd' && 'PPD Code *'}
                    {bulkAction === 'dun' && 'DUN Code *'}
                    {bulkAction === 'par' && 'Parliament Code *'}
                  </label>
                  <input
                    id={`${bulkAction}Code`}
                    type="text"
                    value={bulkAction === 'ppd' ? bulkData.ppd : bulkAction === 'dun' ? bulkData.dunCode : bulkData.parCode}
                    onChange={(e) => {
                      const fieldName = bulkAction === 'ppd' ? 'ppd' : bulkAction === 'dun' ? 'dunCode' : 'parCode';
                      setBulkData({ ...bulkData, [fieldName]: e.target.value.trim().toUpperCase() });
                    }}
                    placeholder={bulkAction === 'ppd'
                      ? 'Enter PPD code (e.g., PPD KENINGAU)'
                      : bulkAction === 'dun'
                        ? 'Enter DUN code (e.g., N01)'
                        : 'Enter Parliament code (e.g., P001)'}
                    maxLength={50}
                    className="form-control"
                  />
                  <small className="form-help">
                    {bulkAction === 'ppd' && 'Enter PPD (Education District) code to map all schools in that district'}
                    {bulkAction === 'dun' && 'Enter DUN (State Legislative Assembly) code to map all schools in that constituency'}
                    {bulkAction === 'par' && 'Enter Parliament constituency code to map all schools in that area'}
                  </small>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="bulkRemarks">Remarks (Optional)</label>
                <textarea
                  id="bulkRemarks"
                  value={bulkData.remarks}
                  onChange={(e) => setBulkData({ ...bulkData, remarks: e.target.value.trim() })}
                  placeholder="Enter remarks for all mapped schools (optional)"
                  maxLength={200}
                  rows={3}
                  className="form-control"
                />
                <small className="form-help">Notes that will be applied to all mappings in this bulk operation</small>
              </div>

              <div className="warning-message">
                <i className="fas fa-exclamation-triangle"></i>
                <div>
                  <p>
                    {bulkAction === 'all' && 'This will map ALL schools in the system to this activity.'}
                    {bulkAction === 'ppd' && `This will map all schools in PPD '${bulkData.ppd || "N/A"}' to this activity.`}
                    {bulkAction === 'dun' && `This will map all schools in DUN '${bulkData.dunCode || "N/A"}' to this activity.`}
                    {bulkAction === 'par' && `This will map all schools in Parliament constituency '${bulkData.parCode || "N/A"}' to this activity.`}
                  </p>
                  <p className="warning-note">
                    All schools will be mapped with eligibility enabled by default.
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowBulkModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleExecuteBulkAction}
                disabled={
                  (bulkAction === 'ppd' && !bulkData.ppd) ||
                  (bulkAction === 'dun' && !bulkData.dunCode) ||
                  (bulkAction === 'par' && !bulkData.parCode)
                }
                className="btn btn-primary btn-bulk-action"
              >
                Execute Bulk Mapping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitySchoolMapping;